<template>
  <div :class="['mine-contents', { opened: isColumnOpened }]" >
    <div
      class="mine-contents-header"
      :style="{ 'padding-right': `${scrollWidth}px` }"
    >
      <div class="column-title report">
        {{ $t(`mine.column.report`) }}
          <button class="sort-button" @click="changeSortBy('title')">
            <i v-if="sort.sortby === 'title' && sort.direction > 0" class="icon icon--spinbox-up"></i>
            <i v-else-if="sort.sortby === 'title' && sort.direction < 0" class="icon icon--spinbox-down"></i>
            <i v-else class="icon icon--spinbox-none"></i>
          </button>
      </div>
      <div class="column-side" :style="{ 'padding-right': `${scrollWidth + 24}px` }">
        <div v-for="(value, key) in columns" :key="key" :class="['column-side-item', key]">
          {{ $t(`mine.column.${key}`) }}
          <button class="sort-button" @click="changeSortBy(value.sortby)">
            <i v-if="sort.sortby === value.sortby && sort.direction > 0" class="icon icon--spinbox-up"></i>
            <i v-else-if="sort.sortby === value.sortby && sort.direction < 0" class="icon icon--spinbox-down"></i>
            <i v-else class="icon icon--spinbox-none"></i>
          </button>
        </div>
        <button class="toggle-button" @click="isColumnOpened = !isColumnOpened">
          <i class="icon icon--arrow-left-black size-12"></i>
        </button>
      </div>
    </div>
    <div ref="scrollArea" class="mine-contents-body" @scroll="showMarketing && $emit('closePopup')">
      <div
        class="mine-report"
        :class="[
          { analyzing: report.reportStatus === 'ing' },
          { error: report.isError }
        ]"
        v-for="report in reportList"
        :key="report.id"
        >
        <div
          v-if="report.type === 'mashup'"
          class="mine-report-mashup"
        >
          <i class="icon icon--filing-iron"></i>
          <div class="mine-mashup-header">
            <div class="mashup-title-wrap">
              <i class="icon icon--mashup size-20"></i>
              <div class="title">
                Mashup
                <span v-if="report.version > 1">v. {{ report.version }}</span>
              </div>
            </div>
            <div class="label-list">
              <div v-if="report.lang" class="lang">
                <i class="icon icon--flag" :class="report.lang"></i>
                <span class="lang-text">{{ report.lang }}</span>
              </div>
              <i v-if="report.new" class="icon new icon--new-small"></i>
            </div>
            <div class="date-view-info">
              <div class="date-info">{{ setRegDate(report.regDate) }}</div>
              <div class="separator"></div>
              <div class="view-count-info">
                <i class="icon icon--eye-white"></i>
                <span>{{ report.viewCount }}</span>
              </div>
            </div>
          </div>
          <div
            v-for="(mashup, i) in mashupMinCount"
            class="mine-report-item report mashup"
            :class="[{ error: report.isError }]"
            :key="`mashup-${mashup}`"
          >
            <template v-if="report.refList[i]">
              <MineListTableItem
                :timezone="domain.timezoneName"
                :mine-data="report.refList[i]"
                :report-type="report.type"
                :opened="isColumnOpened"
                :origin-status="originStatus(report)"
                :mashup-num="i + 1"
                :keyword="keyword"
                :show-marketing="showMarketing"
                @popup="setPopup"
              />
            </template>
            <template v-else>
              <div class="no-report-contents">
                <i class="icon icon--report-empty"></i>
                <div class="title">
                  {{ $t('mine.sideTab.mashup.notSelectLast') }}
                </div>
              </div>
            </template>
          </div>
          <div class="goto-button-wrap">
            <div class="goto-button" @click="onShowReport(report)">
              <i class="icon icon--arrow-right-blue"></i>
              <span>{{ $t('common.view') }}</span>
            </div>
          </div>
          <div
            v-if="!report.isError && report.isReportError"
            class="retry-button btf-button primary icon"
            @click.stop="retry(report)"
          >
            <i class="icon icon--refresh-white"></i>
            {{ $t('common.retry') }}
          </div>
        </div>
        <div v-else class="mine-report-item report">
          <MineListTableItem
            :mine-data="report"
            :timezone="domain.timezoneName"
            :report-type="report.type"
            :opened="isColumnOpened"
            :origin-status="originStatus(report)"
            :keyword="keyword"
            :show-marketing="showMarketing"
            @popup="setPopup"
            @retry="retry"
            @changeTitleEditMode="changeTitleEditMode"
          />
          <div class="goto-button-wrap">
            <div class="goto-button" @click="onShowReport(report)">
              <i class="icon icon--arrow-right-blue"></i>
              <span>{{ $t('common.view') }}</span>
            </div>
          </div>
        </div>
        <button class="toggle-button" @click="isColumnOpened = !isColumnOpened">
          <i class="icon icon--arrow-left-black size-12"></i>
        </button>
      </div>
      <div class="gradient-box"></div>
    </div>
  </div>
</template>

<script>
import MineListTableItem from './MineListTableItem.vue'

import { mapState } from 'vuex'
import { format, parse } from 'date-fns'
import { REPORT_LINK, SOLOMON_REPORT } from '@/libs/constants'
import { isInIframe } from '@/libs/auth-helper'

export default {
  name: 'MineListTableView',
  components: { MineListTableItem },
  props: {
    reportList: {
      type: Array,
      required: true
    },
    keyword: {
      type: String,
      default: ''
    },
    showMarketing: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      mashupMinCount: 2,
      isColumnOpened: false,
      sort: {
        sortby: null,
        direction: 0
      },
      columns: {
        type: { name: 'Type', sortby: 'type' },
        domain: { name: 'Domain', sortby: 'domain' },
        period: { name: 'Period', sortby: 'period' },
        marketing: { name: 'Marketing', sortby: 'marketing' },
        device: { name: 'Device', sortby: 'device' },
        refer: { name: 'Referrer', sortby: 'refer' },
        exist: { name: 'User Type', sortby: 'exist' }
      },
      scrollWidth: 0
    }
  },
  computed: {
    ...mapState(['domain']),
    columnList () {
      if (!this.isColumnOpened) {
        return Object.fromEntries(
          Object.entries(this.columns).filter(([key]) =>
            ['report', 'type'].includes(key)
          )
        )
      }
      return this.columns
    }
  },
  watch: {
    reportList () {
      this.checkScroll()
    }
  },
  mounted () {
    this.checkScroll()
  },
  methods: {
    originStatus (report) {
      return {
        isError: report.isError,
        isReportError: report.isReportError,
        isAnalysis: report.reportStatus === 'ing',
        isNew: report.new
      }
    },
    isClickable (report) {
      const isInvalidReport =
        report.isError || report.isReportError || report.reportStatus === 'ing'
      return !isInvalidReport
    },
    retry (report) {
      this.$emit('retry', report)
    },
    setPopup (data) {
      this.$emit('setPopup', data)
    },
    onShowReport (report) {
      if (!this.isClickable(report)) {
        return null
      } else {
        console.log(report.type)
        if (report.type === 'cxTrends') {
          window.open(
            SOLOMON_REPORT + `/${report.type}/${report.id}`,
            '_blank'
          )
        } else {
          window.open(REPORT_LINK + '/' + report.id, isInIframe() ? '_self' : '_blank')
          this.isReportClicking = false
        }
      }
    },
    setRegDate (utcDate) {
      const date = parse(utcDate, this.timezone)
      return format(date, 'YYYY-MM-DD HH:mm:ss')
    },
    changeTitleEditMode (reportId, value) {
      const report = this.reportList.find(report => report.id === reportId)
      if (report) {
        this.$set(report, 'isTitleEditMode', value)
      }
    },
    changeSortBy (sortby) {
      if (this.sort.sortby === sortby && this.sort.direction > 0) {
        this.sort = { sortby, direction: -1 }
      } else {
        this.sort = { sortby, direction: 1 }
      }

      this.$emit('onSort', this.sort)
    },
    checkScroll () {
      this.$nextTick(() => {
        const scrollAreaEl = this.$refs.scrollArea
        if (scrollAreaEl) this.scrollWidth = scrollAreaEl.offsetWidth - scrollAreaEl.clientWidth
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$analyzeColor: #b65efc;

.mine-contents {
  padding: 0 36px;
  height: calc(100% - 82px);
  & * {
    box-sizing: border-box;
  }
  .toggle-button {
    display: flex;
    position: absolute;
    padding: 8px;
    background-color: #fff;
    border-radius: 100%;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.2);
    z-index: 1;
    &:hover {
      background-color: #f4f4f4;
    }
    &:active {
      background-color: #e6e6e6;
    }
  }

  &-header {
    position: relative;
    display: flex;
    width: 100%;
    height: 52px;
    margin-bottom: 1px;
    color: #fff;
    font-size: 13px;
    background-color: #555;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    overflow: hidden;

    & * {
      transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
    }
    .column-title {
      display: flex;
      align-items: flex-end;
      gap: 5px;
      flex: 1;
      padding: 8px 35px;
      border-top-left-radius: 10px;
      background-color: #333;
      .sort-button {
        display: flex;
      }
    }
    .column-side {
      position: absolute;
      right: 0;
      display: flex;
      align-items: flex-end;
      gap: 6px;
      height: 100%;
      padding: 0 24px 8px 0;
      background: #555;
      transform: translateX(534px);
      .column-side-item {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        box-sizing: initial;
        &.type {
          width: 75px;
          padding-left: 24px;
          transform: translateX(20px);
        }
        &.domain {
          width: 84px;
          padding: 0 15px;
          transform: translateX(200px);
        }
        &.period {
          width: 91px;
          padding: 0 12px;
          transform: translateX(200px);
        }
        &.marketing {
          width: 58px;
          padding: 0 14px;
        }
        &.device {
          width: 50px;
          padding: 0 10px;
        }
        &.refer {
          width: 67px;
          padding: 0 12px;
        }
        &.exist {
          width: 67px;
          padding: 0 121px 0 12px;
        }
      }
      .toggle-button {
        left: 0;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }
  &-body {
    position: relative;
    max-height: calc(100% - 77px);
    background: #fff;
    border-bottom: 26px solid #fff;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2));

    .gradient-box {
      content: '';
      position: sticky;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 16px;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%);
      z-index: 1;
    }
    .mine-report {
      position: relative;
      padding: 0 24px;
      .toggle-button {
        display: none;
        right: 273px;
        top: 50%;
        transform: translate(-50%, -50%);
        transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
        box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 5px 6px -2px rgba(0, 0, 0, 0.16);
        z-index: 2;
      }

      .goto-button-wrap {
        position: absolute;
        top: 5px;
        right: 0;
        width: 115px;
        height: calc(100% - 10px);
        background-color: #FFF;
        z-index: 1;
        &:after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          transform: translateX(100%);
          width: 24px;
          height: 100%;
          background-color: white;
        }
        .goto-button {
          position: absolute;
          top: 50%;
          left: 15px;
          transform: translateY(-50%);
          @include flexXYcenter;
          height: 32px;
          padding: 0 12px 0 10px;
          gap: 4px;
          border-radius: 5px;
          border: 1px solid #0085FF;
          background-color: #FFF;
          color: #0085FF;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          z-index: 2;
          transition: none;
          line-height: 1;
          &:hover {
            transition: none;
            background-color: #0085FF;
            color: #fff;
            .icon {
              filter: brightness(0) invert(1);
            }
          }
        }
      }

      &.error, &.analyzing {
        .goto-button-wrap {
          opacity: 0.3;
          pointer-events: none;
        }
      }

      &:hover {
        .mine-report-item {
          background-color: #f4f4f4;
          .goto-button-wrap {
            background-color: #f4f4f4;
          }
        }
        .mine-report-mashup {
          background-color: #f4f4f4;
          border-top-right-radius: 10px;
          border-bottom-right-radius: 10px;
          .mine-mashup-header {
            display: flex;
          }
          .goto-button-wrap {
            background-color: #f4f4f4;
          }
        }
        &.analyzing, &.error {
          .mine-report-mashup {
            .mine-mashup-header {
              background-color: #777;
            }
          }
        }
        .toggle-button {
          display: flex;
        }
      }
    }
  }
  .mine-report-item {
    position: relative;
    &.report {
      width: 100%;
    }
    .no-report-contents {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #767676;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.5;
      max-width: 130px;
      word-wrap: break-word;
    }
  }
  .mine-report-mashup {
    position: relative;
    .icon--filing-iron {
      position: absolute;
      top: 50%;
      left: -4px;
      transform: translate(-100%, -50%);
    }
    &:after {
      content: '';
      position: absolute;
      top: 0px;
      left: -24px;
      width: calc(100% + 22px);
      height: calc(100% - 5px);
      box-sizing: content-box;
      border: 2px solid #333;
      border-radius: 10px;
      z-index: 1;
      pointer-events: none;
    }
    .mine-mashup-header {
      display: none;
      position: absolute;
      left: 6px;
      top: 50%;
      transform: translateY(-50%);
      align-items: center;
      gap: 8px;
      padding: 1px 8px;
      background-color: #333;
      color: #fff;
      border-radius: 4px;
      z-index: 2;

      & .mashup-title-wrap {
        .title {
          font-size: 13px;
          font-weight: 600;
        }
      }
      & .label-list {
        display: flex;
        gap: 0;
      }
      & .lang {
        position: relative;
        display: flex;
        width: 44px;
        height: 20px;
        padding: 4px 2px;
        margin-right: 8px;
        justify-content: center;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        background: #fff;
        &:after {
          position: absolute;
          top: 50%;
          right: -9px;
          content: '';
          width: 1px;
          height: 8px;
          background: #fff;
          transform: translateY(-50%);
          opacity: 0.2;
        }

        & .lang-text {
          text-transform: uppercase;
          color: #666;
          text-align: center;
          font-size: 12px;
          font-style: normal;
          font-weight: 600;
          line-height: 100%;
          display: inline-block;
        }
      }

      & .new {
        margin-left: 8px;
      }

      & .separator {
        display: inline-block;
        width: 1px;
        height: 8px;
        opacity: 0.2;
        margin: 0 4px;
        background-color: #fff;
      }

      & > div {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .state-label {
        position: relative;
        display: inline-block;
        color: #fff;
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        padding: 4px 6px;
        vertical-align: baseline;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.8);
      }
      .date-view-info {
        color: #bbb;
        font-size: 12px;
        font-weight: 500;
        line-height: 1;
        .view-count-info {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
    }
    .retry-button {
      position: absolute;
      top: 50%;
      left: calc((100% - 200px)/2);
      transform: translate(-50%, -50%);
      z-index: 3;
    }
  }
  &.opened {
    .mine-contents-header {
      .column-side {
        transform: translateX(0);
        .column-side-item {
          &.type {
            transform: translateX(0);
          }
          &.domain {
            padding: 0 15px;
            transform: translateX(0);
          }
          &.period {
            transform: translateX(0);
          }
        }
        .toggle-button {
          .icon {
            rotate: 180deg;
          }
        }
      }
    }
    .mine-contents-body {
      .mine-report {
        .toggle-button {
          transform: translate(-533px, -50%) translateX(-50%);
          .icon {
            rotate: 180deg;
          }
        }
        .retry-button {
          right: calc(726px / 2);
          left: auto;
          transform: translate(50%, -50%);
        }
      }
    }
  }
  .ghost {
    display: block;
    position: absolute;
    top: 0;
    right: 100%;
    z-index: -99;
    width: 800px;
    .mine-report-item, .mine-report-mashup {
      opacity: .7;
      transform-origin: top left;
      transform: scale(0.8);
      border-radius: 10px;
      border: none;
      background-color: #fff;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 18px 22px -7px rgba(0, 0, 0, 0.16);
      overflow: hidden;
      .mine-report-item {
        opacity: 1;
        transform: scale(1);
      }
      &:after {
        display: none;
      }
    }
    .toggle-button {
      display: none;
    }
    .goto-button-wrap {
      display: none;
    }
    &.visible {
      display: block;
    }
  }
}
</style>
